import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { AppSettings, defaults } from '../app.config';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { saveAs } from 'file-saver';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class CoreService {
  private baseUrl = environment.djangoApiUrl;
  
  constructor(private http: HttpClient, public router: Router) {
  }
  get notify(): Observable<Record<string, any>> {
    return this.notify$.asObservable();
  }


  private extractData(res:any) {
    const  body = res;
    return body || { };
  }
  downloadFile(url: string, filename: string): void {
    this.http.get(url, { responseType: 'blob' }).subscribe(blob => {
      saveAs(blob, filename);
    }, error => {
      console.error('Download error:', error);
    });
  }

  // Step 1: Initiate login with email and password
  initiateLogin(data: any): Observable<any> {
    return this.http.post(this.baseUrl + '/login/', data).pipe(
      map(this.extractData));
  }

  // Step 2: Complete login with OTP verification
  completeLogin(data: any): Observable<any> {
    return this.http.post(this.baseUrl + '/login/complete/', data).pipe(
      map(this.extractData));
  }

  // Legacy login method (keeping for backward compatibility)
  login(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/login/', data).pipe(
      map(this.extractData));
  }


  
  getCompanies(params?: string):Observable<any>{
    let url = this.baseUrl + '/companies/';
    if (params) {
      url += params;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }


  getCompanyDetails(companyId: string):Observable<any>{
    return this.http.get(this.baseUrl + `/companies/${companyId}/`).pipe(
      map(this.extractData));
  }


  // Dashboard Top Cards API method
  getDashboardTopCards(currency: string = 'KES'):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/topcards/?currency=${currency}`).pipe(
      map(this.extractData));
  }


  // M-Pesa Transactions API method for payments list
  getMpesaTransactionsList(params: string = '', gatewayId?: string, searchTerm?: string, companyId?: string, page?: number, limit?: number): Observable<any> {
    let url = this.baseUrl + '/mpesa_transactions/';
    const queryParams: string[] = [];
    
    if (params) {
      url += params;
    }
    if (gatewayId) {
      queryParams.push(`gateway=${gatewayId}`);
    }
    if (companyId) {
      queryParams.push(`gateway__company=${companyId}`);
    }
    if (searchTerm && searchTerm.trim()) {
      queryParams.push(`search=${encodeURIComponent(searchTerm.trim())}`);
    }
    if (page) {
      queryParams.push(`page=${page}`);
    }
    if (limit) {
      queryParams.push(`limit=${limit}`);
    }
    
    if (queryParams.length > 0) {
      url += (params ? '&' : '?') + queryParams.join('&');
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Gateways API method
  getGateways(companyId?: string):Observable<any>{
    let url = this.baseUrl + '/gateways/';
    if (companyId) {
      url += `?company=${companyId}`;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Gateway Chart Data API method
  getGatewayChartData(currency: string = 'KES', limit: number = 10):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/gateway-chart/?currency=${currency}&limit=${limit}`).pipe(
      map(this.extractData));
  }

  // Get Hourly Chart Data API method
  getHourlyChartData(currency: string = 'KES', hoursBack: number = 24):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/hourly-chart/?currency=${currency}&hours_back=${hoursBack}`).pipe(
      map(this.extractData));
  }

  // Get Companies Chart Data API method
  getCompaniesChartData(currency: string = 'KES', limit: number = 20, sortBy: string = 'volume'):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/companies-chart/?currency=${currency}&limit=${limit}&sort_by=${sortBy}`).pipe(
      map(this.extractData));
  }

  // Company Users API method
  getCompanyUsers(companyId: string):Observable<any>{
    return this.http.get(this.baseUrl + `/companies/${companyId}/users/`).pipe(
      map(this.extractData));
  }

  // Promotion API methods
  getPromotions():Observable<any>{
    return this.http.get(this.baseUrl + '/promotions/').pipe(
      map(this.extractData));
  }

  getPromotion(id: string):Observable<any>{
    return this.http.get(this.baseUrl + `/promotions/${id}/`).pipe(
      map(this.extractData));
  }

  createPromotion(data: any):Observable<any>{
    return this.http.post(this.baseUrl + '/promotions/', data).pipe(
      map(this.extractData));
  }

  updatePromotion(id: string, data: any):Observable<any>{
    return this.http.put(this.baseUrl + `/promotions/${id}/`, data).pipe(
      map(this.extractData));
  }

  deletePromotion(id: string):Observable<any>{
    return this.http.delete(this.baseUrl + `/promotions/${id}/`).pipe(
      map(this.extractData));
  }

  // Gateway API methods
  getGateway(id: string):Observable<any>{
    return this.http.get(this.baseUrl + `/gateways/${id}/`).pipe(
      map(this.extractData));
  }

  getGatewaysList():Observable<any>{
    return this.http.get(this.baseUrl + '/gateways/').pipe(
      map(this.extractData));
  }

  // Company Payment Stats API method
  getCompanyPaymentStats(
    companyId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      hub_id?: string;
    }
  ):Observable<any>{
    let url = this.baseUrl + `/companies/${companyId}/payment-stats/`;
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Company Revenue Chart API method
  getCompanyRevenueChart(
    companyId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      hub_id?: string;
      period?: string;
    }
  ):Observable<any>{
    let url = this.baseUrl + `/companies/${companyId}/revenue-chart/`;
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Delete Company API method
  deleteCompany(id: string):Observable<any>{
    return this.http.delete(this.baseUrl + `/companies/${id}/`).pipe(
      map(this.extractData));
  }

  // STK Logs API method
  getStkLogs(params: any):Observable<any>{
    let url = this.baseUrl + '/stk_logs/';
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Company Payments Report API method
  getCompanyPaymentsReport(companyId: string, params: any):Observable<any>{
    let url = this.baseUrl + `/reports/dashboard/company-payments-report/?company_id=${companyId}`;
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    if (queryParams.toString()) {
      url += '&' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Payment Processing Report API method
  getPaymentProcessingReport(params: any):Observable<any>{
    let url = this.baseUrl + '/reports/dashboard/payment-processing-report/';
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value.toString());
      }
    });
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Delete Gateway API method
  deleteGateway(id: string):Observable<any>{
    return this.http.delete(this.baseUrl + `/gateways/${id}/`).pipe(
      map(this.extractData));
  }

  // Update Gateway API method
  updateGateway(id: string, data: any):Observable<any>{
    return this.http.put(this.baseUrl + `/gateways/${id}/`, data).pipe(
      map(this.extractData));
  }

  // Get Transaction Chart Data API method
  getTransactionChartData(currency: string = 'KES', timePeriod: string = 'monthly', dataPoints: number = 8):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/transaction-chart/?currency=${currency}&time_period=${timePeriod}&data_points=${dataPoints}`).pipe(
      map(this.extractData));
  }

  // Get Payment Chart Data API method
  getPaymentChartData(currency: string = 'KES', timePeriod: string = 'today'):Observable<any>{
    return this.http.get(this.baseUrl + `/reports/dashboard/payment-requests-conversion/?currency=${currency}&time_period=${timePeriod}`).pipe(
      map(this.extractData));
  }

  // Get Financial Report API method (Legacy - Helapay)
  getFinancialReport(timePeriod: string = '6_months', currency: string = 'KSH', companyId: string = '', gatewayId: string = ''):Observable<any>{
    let url = this.baseUrl + `/reports/dashboard/financial-report/?time_period=${timePeriod}&currency=${currency}`;
    if (companyId && companyId.trim() !== '') {
      url += `&company_id=${companyId}`;
    }
    if (gatewayId && gatewayId.trim() !== '') {
      url += `&gateway_id=${gatewayId}`;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // DeliverFasta API Methods
  // Get Orders
  getOrders(params?: string):Observable<any>{
    let url = this.baseUrl + '/orders/';
    if (params) {
      url += params;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Driver Verified Orders
  getDriverVerifiedOrders(driverId: string):Observable<any>{
    const url = this.baseUrl + `/orders/driver-verified-orders/?driver_id=${driverId}`;
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get DeliverFasta Payments
  getDeliverFastaPayments(params?: string):Observable<any>{
    let url = this.baseUrl + '/payments/';
    if (params) {
      url += params;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Hubs
  getHubs(params?: string):Observable<any>{
    let url = this.baseUrl + '/hubs/';
    if (params) {
      url += params;
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Hub Reports (based on REPORTS_SPECIFICATION.md)
  // Get Hub Analytics Summary
  getHubAnalytics(hubId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/hubs/${hubId}/analytics/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Hub Revenue Chart
  getHubRevenueChart(hubId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/hubs/${hubId}/revenue-chart/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Hub Orders Chart
  getHubOrdersChart(hubId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/hubs/${hubId}/orders-chart/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Hub Inventory Status Chart
  getHubInventoryStatusChart(hubId: string):Observable<any>{
    return this.http.get(this.baseUrl + `/hubs/${hubId}/inventory-status-chart/`).pipe(
      map(this.extractData));
  }

  // Product Reports (based on REPORTS_SPECIFICATION.md)
  // Get Product Sales Analytics
  getProductSalesAnalytics(productId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/products/${productId}/sales-analytics/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Product Sales Chart
  getProductSalesChart(productId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/products/${productId}/sales-chart/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Products Sold Report
  getProductsSold(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    hub_id?: string;
    page?: number;
    page_size?: number;
  }):Observable<any>{
    let url = this.baseUrl + '/reports/products/products-sold/';
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.start_date && params.end_date) {
        queryParams.append('start_date', params.start_date);
        queryParams.append('end_date', params.end_date);
      } else if (params.period) {
        queryParams.append('period', params.period);
      } else {
        queryParams.append('period', 'last_30_days');
      }
      
      if (params.hub_id && params.hub_id !== 'all') {
        queryParams.append('hub_id', params.hub_id);
      }
      
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      
      if (params.page_size) {
        queryParams.append('page_size', params.page_size.toString());
      }
    } else {
      queryParams.append('period', 'last_30_days');
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Hub Completed Orders Report
  getHubCompletedOrders(params?: {
    period?: string;
    start_date?: string;
    end_date?: string;
    hub_id?: string;
    status?: string;
    page?: number;
    page_size?: number;
  }):Observable<any>{
    let url = this.baseUrl + '/reports/hubs/completed-orders/';
    const queryParams = new URLSearchParams();
    
    if (params) {
      if (params.start_date && params.end_date) {
        queryParams.append('start_date', params.start_date);
        queryParams.append('end_date', params.end_date);
      } else if (params.period) {
        queryParams.append('period', params.period);
      } else {
        queryParams.append('period', 'last_30_days');
      }
      
      if (params.hub_id && params.hub_id !== 'all') {
        queryParams.append('hub_id', params.hub_id);
      }
      
      if (params.status) {
        queryParams.append('status', params.status);
      }
      
      if (params.page) {
        queryParams.append('page', params.page.toString());
      }
      
      if (params.page_size) {
        queryParams.append('page_size', params.page_size.toString());
      }
    } else {
      queryParams.append('period', 'last_30_days');
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Payment Method Reports
  // Get Payment Method Analytics
  getPaymentMethodAnalytics(paymentMethodId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/payment-methods/${paymentMethodId}/analytics/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Get Payment Method Chart
  getPaymentMethodChart(paymentMethodId: string, period: string):Observable<any>{
    return this.http.get(this.baseUrl + `/payment-methods/${paymentMethodId}/chart/?period=${period}`).pipe(
      map(this.extractData));
  }

  // Inventory Reports (based on REPORTS_SPECIFICATION.md)
  // Get Inventory Stock History
  getInventoryStockHistory(
    inventoryId: string,
    params?: {
      transaction_type?: string;
      start_date?: string;
      end_date?: string;
      page?: number;
      page_size?: number;
    }
  ):Observable<any>{
    let url = this.baseUrl + `/inventory/${inventoryId}/stock-history/`;
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Inventory Transfers
  getInventoryTransfers(
    inventoryId: string,
    params?: {
      type?: string;
      status?: string;
      page?: number;
      page_size?: number;
    }
  ):Observable<any>{
    let url = this.baseUrl + `/inventory/${inventoryId}/transfers/`;
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Sales Report (DeliverFasta) - Updated to match REPORTS_SPECIFICATION.md
  getSalesReport(params: {
    period: string;
    hub_id?: string;
  }):Observable<any>{
    let url = this.baseUrl + '/reports/sales/';
    const queryParams = new URLSearchParams();
    queryParams.append('period', params.period);
    
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    
    url += '?' + queryParams.toString();
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  /**
   * Get daily summary report for a given date.
   * Expected API: GET /reports/daily-summary/?date=YYYY-MM-DD
   */
  getDailySummaryReport(params: { date: string }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('date', params.date);
    const url = this.baseUrl + '/reports/daily-summary/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Download daily summary export file for a given date.
   * GET /reports/daily-summary/export/?date=YYYY-MM-DD
   */
  getDailySummaryExport(date: string): Observable<Blob> {
    const url = this.baseUrl + '/reports/daily-summary/export/?date=' + encodeURIComponent(date);
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Monthly bales delivered grid report.
   * GET /api/reports/monthly-bales-delivered/?year=2026&month=6
   */
  getMonthlyBalesDeliveredReport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    const url = this.baseUrl + '/reports/monthly-bales-delivered/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for monthly bales delivered report.
   * GET /api/reports/monthly-bales-delivered/export/?year=2026&month=6
   */
  getMonthlyBalesDeliveredExport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    const url = this.baseUrl + '/reports/monthly-bales-delivered/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Monthly bales deviation from target grid report.
   * GET /api/reports/monthly-bales-deviation/?year=2026&month=6
   */
  getMonthlyBalesDeviationReport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    const url = this.baseUrl + '/reports/monthly-bales-deviation/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for monthly bales deviation report.
   * GET /api/reports/monthly-bales-deviation/export/?year=2026&month=6
   */
  getMonthlyBalesDeviationExport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    const url = this.baseUrl + '/reports/monthly-bales-deviation/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Monthly driver MTD performance summary.
   * GET /api/reports/monthly-driver-mtd/?year=2026&month=6
   */
  getMonthlyDriverMtdReport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
    as_of_date?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-driver-mtd/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for monthly driver MTD report.
   * GET /api/reports/monthly-driver-mtd/export/?year=2026&month=6
   */
  getMonthlyDriverMtdExport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
    as_of_date?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-driver-mtd/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Monthly hub & spoke MTD summary.
   * GET /api/reports/monthly-hub-mtd/?year=2026&month=4
   */
  getMonthlyHubMtdReport(params: {
    year: number;
    month: number;
    hub_id?: string;
    as_of_date?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-hub-mtd/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for monthly hub MTD report.
   * GET /api/reports/monthly-hub-mtd/export/?year=2026&month=4
   */
  getMonthlyHubMtdExport(params: {
    year: number;
    month: number;
    hub_id?: string;
    as_of_date?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-hub-mtd/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Monthly hub & spoke summary (per rider).
   * GET /api/reports/monthly-hub-summary/?year=2026&month=4
   */
  getMonthlyHubSummaryReport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
    as_of_date?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-hub-summary/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for monthly hub summary report.
   * GET /api/reports/monthly-hub-summary/export/?year=2026&month=4
   */
  getMonthlyHubSummaryExport(params: {
    year: number;
    month: number;
    hub_id?: string;
    driver_id?: string;
    as_of_date?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    queryParams.append('month', String(params.month));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params.as_of_date) {
      queryParams.append('as_of_date', params.as_of_date);
    }
    const url = this.baseUrl + '/reports/monthly-hub-summary/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * Yearly monthly performance summary (one row per month).
   * GET /api/reports/yearly-monthly-performance/?year=2026
   */
  getYearlyMonthlyPerformanceReport(params: {
    year: number;
    hub_id?: string;
  }): Observable<any> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    const url = this.baseUrl + '/reports/yearly-monthly-performance/?' + queryParams.toString();
    return this.http.get(url).pipe(map(this.extractData));
  }

  /**
   * Excel export for yearly monthly performance report.
   * GET /api/reports/yearly-monthly-performance/export/?year=2026
   */
  getYearlyMonthlyPerformanceExport(params: {
    year: number;
    hub_id?: string;
  }): Observable<Blob> {
    const queryParams = new URLSearchParams();
    queryParams.append('year', String(params.year));
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    const url = this.baseUrl + '/reports/yearly-monthly-performance/export/?' + queryParams.toString();
    return this.http.get(url, { responseType: 'blob' });
  }

  /**
   * List target settings (day-of-week targets). Response: { results: [...] }.
   * GET /api/reports/target-settings/
   */
  getTargetSettings(): Observable<{ results: any[] }> {
    return this.http.get(this.baseUrl + '/reports/target-settings/').pipe(map(this.extractData));
  }

  /**
   * Get one target setting by id.
   * GET /api/reports/target-settings/<id>/
   */
  getTargetSetting(id: string | number): Observable<any> {
    return this.http.get(this.baseUrl + '/reports/target-settings/' + id + '/').pipe(map(this.extractData));
  }

  /**
   * Update one target setting (partial). Body: orders_target, bales_target, retailers_target, etc.
   * PATCH /api/reports/target-settings/<id>/
   */
  patchTargetSetting(id: string | number, body: { orders_target?: number; bales_target?: number; retailers_target?: number; is_active?: boolean; day_of_week?: number | null }): Observable<any> {
    return this.http.patch(this.baseUrl + '/reports/target-settings/' + id + '/', body).pipe(map(this.extractData));
  }

  /**
   * Create a new target setting. Body: day_of_week (0–6 or null for default), orders_target, bales_target, retailers_target.
   * POST /api/reports/target-settings/
   */
  createTargetSetting(body: { day_of_week?: number | null; orders_target: number; bales_target: number; retailers_target: number }): Observable<any> {
    return this.http.post(this.baseUrl + '/reports/target-settings/', body).pipe(map(this.extractData));
  }

  /**
   * Delete one target setting.
   * DELETE /api/reports/target-settings/<id>/
   */
  deleteTargetSetting(id: string | number): Observable<void> {
    return this.http.delete(this.baseUrl + '/reports/target-settings/' + id + '/').pipe(map(() => undefined));
  }

  /**
   * List vehicle operating cost settings.
   * GET /api/operating-cost-settings/
   */
  getOperatingCostSettings(): Observable<{ results: any[] }> {
    return this.http.get(this.baseUrl + '/operating-cost-settings/').pipe(map(this.extractData));
  }

  /**
   * Get one operating cost setting.
   * GET /api/operating-cost-settings/<id>/
   */
  getOperatingCostSetting(id: string | number): Observable<any> {
    return this.http.get(this.baseUrl + '/operating-cost-settings/' + id + '/').pipe(map(this.extractData));
  }

  /**
   * Create operating cost setting.
   * POST /api/operating-cost-settings/
   */
  createOperatingCostSetting(body: {
    vehicle_type?: string | null;
    monthly_cost: string | number;
    is_active?: boolean;
    notes?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + '/operating-cost-settings/', body).pipe(map(this.extractData));
  }

  /**
   * Update operating cost setting.
   * PATCH /api/operating-cost-settings/<id>/
   */
  patchOperatingCostSetting(
    id: string | number,
    body: {
      vehicle_type?: string | null;
      monthly_cost?: string | number;
      is_active?: boolean;
      notes?: string;
    }
  ): Observable<any> {
    return this.http.patch(this.baseUrl + '/operating-cost-settings/' + id + '/', body).pipe(map(this.extractData));
  }

  /**
   * Delete operating cost setting.
   * DELETE /api/operating-cost-settings/<id>/
   */
  deleteOperatingCostSetting(id: string | number): Observable<void> {
    return this.http.delete(this.baseUrl + '/operating-cost-settings/' + id + '/').pipe(map(() => undefined));
  }

  // Get Driver Commission Report
  getDriverCommissionReport(params: {
    period?: string;
    start_date?: string;
    end_date?: string;
    driver_id?: string;
    hub_id?: string;
  }):Observable<any>{
    let url = this.baseUrl + '/dashboard/driver-commissions/';
    const queryParams = new URLSearchParams();
    
    if (params.period) {
      queryParams.append('period', params.period);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    if (params.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Hub Commission Report (single hub or system-wide)
  getHubCommissionReport(params: {
    hub_id?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
  }):Observable<any>{
    let url: string;
    if (params.hub_id) {
      url = this.baseUrl + `/hubs/${params.hub_id}/commissions/`;
    } else {
      // System-wide hub commissions
      url = this.baseUrl + '/reports/hub-commissions/';
    }
    const queryParams = new URLSearchParams();
    
    if (params.period) {
      queryParams.append('period', params.period);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Get Company Commission Report (single company or system-wide)
  getCompanyCommissionReport(params: {
    company_id?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    hub_id?: string;
  }):Observable<any>{
    let url: string;
    if (params.company_id) {
      url = this.baseUrl + `/companies/${params.company_id}/commissions/`;
    } else {
      // System-wide company commissions
      url = this.baseUrl + '/reports/company-commissions/';
    }
    const queryParams = new URLSearchParams();
    
    if (params.period) {
      queryParams.append('period', params.period);
    }
    if (params.start_date) {
      queryParams.append('start_date', params.start_date);
    }
    if (params.end_date) {
      queryParams.append('end_date', params.end_date);
    }
    if (params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Driver Retailer Visits Report
  getDriverRetailerVisitsReport(params?: {
    driver_id?: string;
    period?: string;
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    hub_id?: string;
    search?: string;
    driver_name?: string;
    name?: string;
  }): Observable<any> {
    let url = this.baseUrl + '/users/driver-retailer-visits/';
    const queryParams = new URLSearchParams();

    if (params?.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params?.period) {
      queryParams.append('period', params.period);
    }
    if (params?.start_date && params?.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
    if (params?.start_time) {
      queryParams.append('start_time', params.start_time);
    }
    if (params?.end_time) {
      queryParams.append('end_time', params.end_time);
    }
    if (params?.hub_id && params.hub_id !== 'all') {
      queryParams.append('hub_id', params.hub_id);
    }
    const nameQuery = params?.search || params?.driver_name || params?.name;
    if (nameQuery) {
      queryParams.append('search', nameQuery);
    }

    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }

    return this.http.get(url).pipe(
      map(this.extractData));
  }

  // Driver Check-in / Check-out Report
  getDriverCheckinCheckoutReport(params?: {
    driver_id?: string;
    driver?: string;
    start_date?: string;
    end_date?: string;
    search?: string;
  }): Observable<any> {
    let url = this.baseUrl + '/users/driver-checkin-checkout/';
    const queryParams = new URLSearchParams();

    if (params?.driver_id) {
      queryParams.append('driver_id', params.driver_id);
    }
    if (params?.driver) {
      queryParams.append('driver', params.driver);
    }
    if (params?.start_date && params?.end_date) {
      queryParams.append('start_date', params.start_date);
      queryParams.append('end_date', params.end_date);
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }

    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }

    return this.http.get(url).pipe(
      map(this.extractData));
  }


  createPaymentRefund(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/payment_refunds/',data).pipe(
      map(this.extractData));
  }

  // Add back methods that are actually being used
  addCompany(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/company/',data).pipe(
      map(this.extractData));
  }

  addEmployee(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/add_employee/',data).pipe(
      map(this.extractData));
  }

  getCategory():Observable<any>{
    return this.http.get(this.baseUrl + '/expense/').pipe(
      map(this.extractData));
  }

  addCategory(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/expense/',data).pipe(
      map(this.extractData));
  }


  getBranch(id:any):Observable<any>{
    return this.http.get(this.baseUrl + '/branches/'+id+'/').pipe(
      map(this.extractData));
  }

  updateBranch(data:any):Observable<any>{
    return this.http.put(this.baseUrl + '/branches/'+data.id+'/',data).pipe(
      map(this.extractData));
  }

  addDepartment(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/department/',data).pipe(
      map(this.extractData));
  }

  getDepartments():Observable<any>{
    return this.http.get(this.baseUrl + '/department/').pipe(
      map(this.extractData));
  }

  getBranchs():Observable<any>{
    return this.http.get(this.baseUrl + '/branches/').pipe(
      map(this.extractData));
  }

  getUsers(text:any):Observable<any>{
    return this.http.get(this.baseUrl + '/users/'+text).pipe(
      map(this.extractData));
  }

  getUser(id:any):Observable<any>{
    return this.http.get(this.baseUrl + '/users/'+id+'/').pipe(
      map(this.extractData));
  }

  updateUser(id:any, data:any):Observable<any>{
    return this.http.put(this.baseUrl + '/users/'+id+'/',data).pipe(
      map(this.extractData));
  }

  getRoles():Observable<any>{
    return this.http.get(this.baseUrl + '/roles/').pipe(
      map(this.extractData));
  }

  getEmploymentTypes():Observable<any>{
    return this.http.get(this.baseUrl + '/employment_types/').pipe(
      map(this.extractData));
  }

  getVehicles(data:any):Observable<any>{
    return this.http.get(this.baseUrl + '/vehicles/'+data).pipe(
      map(this.extractData));
  }

  getPayments(data:any):Observable<any>{
    return this.http.get(this.baseUrl + '/payments/'+data).pipe(
      map(this.extractData));
  }

  createInvoice(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/invoices/',data).pipe(
      map(this.extractData));
  }

  getUnits(data:any):Observable<any>{
    return this.http.get(this.baseUrl + '/units/'+data).pipe(
      map(this.extractData));
  }

  addAccounts(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/accounts/',data).pipe(
      map(this.extractData));
  }

  getAccounts(data:any):Observable<any>{
    return this.http.get(this.baseUrl + '/accounts/'+data).pipe(
      map(this.extractData));
  }

  serviceTypeCategory(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/service_types/',data).pipe(
      map(this.extractData));
  }

  getProfile():Observable<any>{
    return this.http.get(this.baseUrl + '/user_details/').pipe(
      map(this.extractData));
  }

  changePassword(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/change_password/',data).pipe(
      map(this.extractData));
  }

  profileUpload(data:any):Observable<any>{
    return this.http.post(this.baseUrl + '/profile_upload/',data).pipe(
      map(this.extractData));
  }

  // Dispatch API Methods (see docs/FRONTEND_API_SPEC.md §4)
  getDispatches(params?: {
    page?: number;
    page_size?: number;
    search?: string;
    order_id?: string;
    driver_id?: string;
    dispatched_by_id?: string;
    driver_confirmed?: boolean;
    driver_rejected?: boolean;
    hub_id?: string;
    created_from?: string;
    created_to?: string;
    ordering?: string;
  }): Observable<any> {
    let url = this.baseUrl + '/dispatches/';
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  getOrdersReadyForDispatch(params?: {
    hub_id?: string;
  }):Observable<any>{
    let url = this.baseUrl + '/dispatches/ready-for-dispatch/';
    const queryParams = new URLSearchParams();
    
    if (params && params.hub_id) {
      queryParams.append('hub_id', params.hub_id);
    }
    
    if (queryParams.toString()) {
      url += '?' + queryParams.toString();
    }
    return this.http.get(url).pipe(
      map(this.extractData));
  }

  createDispatch(data: {
    orders: string[];
    driver_id: string;
    notes?: string;
  }):Observable<any>{
    return this.http.post(this.baseUrl + '/dispatches/', data).pipe(
      map(this.extractData));
  }

  /** Create pending dispatch + send handover OTP to hub contact number */
  requestDispatchOtp(data: {
    orders: string[];
    driver_id: string;
    notes?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + '/dispatches/request-otp/', data).pipe(
      map(this.extractData));
  }

  /** Resend handover OTP (max 3). Pass challenge_id or dispatch_id. */
  resendDispatchOtp(data: {
    challenge_id?: string;
    dispatch_id?: string;
  }): Observable<any> {
    return this.http.post(this.baseUrl + '/dispatches/resend-otp/', data).pipe(
      map(this.extractData));
  }

  /** GET /api/dispatches/otp-config/ — dynamic vs static OTP settings */
  getDispatchOtpConfig(): Observable<{
    id: string | null;
    mode: 'dynamic' | 'static';
    static_code: string;
    is_active: boolean;
    notes?: string;
    updated_by?: string | null;
    updated_by_name?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
    message?: string;
  }> {
    return this.http.get(this.baseUrl + '/dispatches/otp-config/').pipe(
      map(this.extractData));
  }

  /** PUT/PATCH /api/dispatches/otp-config/ */
  updateDispatchOtpConfig(data: {
    mode?: 'dynamic' | 'static';
    static_code?: string;
    is_active?: boolean;
    notes?: string;
  }): Observable<any> {
    return this.http.patch(this.baseUrl + '/dispatches/otp-config/', data).pipe(
      map(this.extractData));
  }

  /** GET /api/app-version/ — authenticated */
  getAppVersion(platform?: 'all' | 'android' | 'ios' | 'web'): Observable<any> {
    const qs = platform ? `?platform=${platform}` : '';
    return this.http.get(this.baseUrl + '/app-version/' + qs).pipe(
      map(this.extractData));
  }

  /** GET /api/app-version/all/ */
  getAllAppVersions(): Observable<any[]> {
    return this.http.get(this.baseUrl + '/app-version/all/').pipe(
      map(this.extractData));
  }

  /** PATCH /api/app-version/ — Admin only */
  setAppVersion(data: {
    platform?: 'all' | 'android' | 'ios' | 'web';
    version?: string;
    build_number?: number | null;
    min_supported_version?: string | null;
    download_link?: string | null;
    force_update?: boolean;
    release_notes?: string | null;
    is_active?: boolean;
  }): Observable<any> {
    return this.http.patch(this.baseUrl + '/app-version/', data).pipe(
      map(this.extractData));
  }

  /** DELETE /api/app-version/?id=… or ?platform=… — Admin only */
  deleteAppVersion(params: { id?: string; platform?: 'all' | 'android' | 'ios' | 'web' }): Observable<any> {
    const qs = params.id
      ? `?id=${encodeURIComponent(params.id)}`
      : params.platform
        ? `?platform=${encodeURIComponent(params.platform)}`
        : '';
    return this.http.delete(this.baseUrl + '/app-version/' + qs).pipe(
      map(this.extractData));
  }

  getDispatch(id: string):Observable<any>{
    return this.http.get(this.baseUrl + `/dispatches/${id}/`).pipe(
      map(this.extractData));
  }

  updateDispatch(id: string, data: any):Observable<any>{
    return this.http.put(this.baseUrl + `/dispatches/${id}/`, data).pipe(
      map(this.extractData));
  }

  deleteDispatch(id: string):Observable<any>{
    return this.http.delete(this.baseUrl + `/dispatches/${id}/`).pipe(
      map(this.extractData));
  }

  /** Rider accepts pending dispatch with hub OTP */
  confirmDispatch(id: string, otp?: string):Observable<any>{
    const body = otp ? { otp } : {};
    return this.http.post(this.baseUrl + `/dispatches/${id}/confirm/`, body).pipe(
      map(this.extractData));
  }

  /**
   * POST /api/dispatches/{id}/hub-receipt/
   * Upload or replace hub receipt (multipart: hub_receipt file).
   */
  uploadDispatchHubReceipt(id: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('hub_receipt', file);
    return this.http.post(this.baseUrl + `/dispatches/${id}/hub-receipt/`, formData).pipe(
      map(this.extractData));
  }

  rejectDispatch(id: string, comments: string):Observable<any>{
    return this.http.post(this.baseUrl + `/dispatches/${id}/reject/`, { comments }).pipe(
      map(this.extractData));
  }

  getMyDispatches():Observable<any>{
    return this.http.get(this.baseUrl + '/dispatches/my-dispatches/').pipe(
      map(this.extractData));
  }

  
  private notify$ = new BehaviorSubject<Record<string, any>>({});

  getOptions() {
    return this.options;
  }

  setOptions(options: AppSettings) {
    this.options = Object.assign(defaults, options);
    this.notify$.next(this.options);
  }

  private options = defaults;

  getLanguage() {
    return this.options.language;
  }

  setLanguage(lang: string) {
    this.options.language = lang;
    this.notify$.next({ lang });
  }
}
